import { isEqual } from "lodash";
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { isQueryType, QueryType } from "src/types/models";
import { getSubsetIds, ModelState, subsetIdsEqual } from "src/utils/models";
import { UnreachableCaseError } from "ts-essentials";
import { useUndo } from "./use-undo";
import { hydrateConditionWithIds } from "src/components/audiences/utils";
import { VisualQueryFilter } from "src/types/visual";
import { removeUndefinedKeysDeep } from "src/utils/object";

export type UseModelStateReturn = ReturnType<typeof useModelState>;

export const processVisualQueryFilter = (
  v: VisualQueryFilter | null | undefined,
  overrides?: Partial<VisualQueryFilter>,
): VisualQueryFilter =>
  hydrateConditionWithIds({
    ...(v ?? {}),
    // Child components expect conditions to be an array
    conditions: v?.conditions ?? [],
    ...(overrides ?? {}),
  });

interface UseModelStateOptions {
  /**
   * By default (true), only resets the baseline if the model source changes.
   * If false, will reset every time the model changes. In this case, the model argument must be referentially stable.
   */
  resetOnlyOnSourceChange?: boolean;
}

export const useModelState = (
  model?: Partial<ModelState> | null,
  args: UseModelStateOptions = {},
) => {
  const initialModel = useMemo(
    () =>
      model
        ? {
            ...model,
            visual_query_filter: processVisualQueryFilter(
              model.visual_query_filter,
            ),
          }
        : null,
    [model],
  );
  const { resetOnlyOnSourceChange = true } = args;
  const ref = useRef<ModelState | null | undefined>(initialModel as ModelState);
  const [history, options] = useUndo<ModelState>(
    (initialModel ?? {}) as ModelState,
    { useCheckpoints: true },
  );
  const { update } = options;
  const state = history.present;
  const [isPreviewFresh, setIsPreviewFresh] = useState(false);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setIsPreviewFresh(false);
  }, [
    state?.query_dbt_model_id,
    state?.query_looker_look_id,
    state?.visual_query_filter,
    state?.query_raw_sql,
    state?.query_table_name,
    state?.query_integrations,
    state?.custom_query,
    state?.query_type,
  ]);

  const isDirty = useMemo(() => !isEqual(state, ref.current), [state]);

  const isValid = useMemo(() => {
    if (isQueryType(state?.query_type)) {
      const queryType: QueryType = state?.query_type;
      switch (queryType) {
        case QueryType.RawSql:
          return Boolean(state?.query_raw_sql);
        case QueryType.Table:
          return Boolean(state?.query_table_name);
        case QueryType.DbtModel:
        case QueryType.DbtCloudModel:
          return Boolean(state?.query_dbt_model_id);
        case QueryType.LookerLook:
          return Boolean(state?.query_looker_look_id);
        case QueryType.Custom:
          return Boolean(state?.custom_query);
        case QueryType.Sigma:
          return Boolean(state?.query_integrations);
        case QueryType.DecisionEngine:
        case QueryType.JourneyNode:
        case QueryType.IDRGoldenRecordParent:
        case QueryType.IDRGoldenRecord:
        case QueryType.Visual:
        case QueryType.Predictive:
        case QueryType.CepManagedModel:
        case QueryType.RealtimeTraitWarehouseState:
        case QueryType.LcsTriggeredSend:
          return true;
        default:
          throw new UnreachableCaseError(queryType);
      }
    }
    return false;
  }, [state]);

  const onChange = useCallback(
    (newState: Partial<ModelState>) => {
      update((previousState) => ({
        ...previousState,
        ...newState,
      }));
    },
    [update],
  );

  const reset = useCallback(
    (state?: ModelState, preservedChanges?: Partial<ModelState>) => {
      if (state) {
        const hydratedState = {
          ...state,
          visual_query_filter: processVisualQueryFilter(
            state.visual_query_filter,
          ),
        };
        ref.current = hydratedState;
        const newState = { ...hydratedState, ...preservedChanges };
        options.reset(newState);
      } else if (ref.current) {
        options.reset(ref.current);
      }
    },
    [options.reset],
  );

  const set = useCallback(
    (state: ModelState, checkpoint?: boolean) => {
      const stateWithIds = {
        ...state,
        visual_query_filter: processVisualQueryFilter(
          state.visual_query_filter,
        ),
      };
      options.set(stateWithIds, checkpoint);
    },
    [options.set],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!initialModel) return;

    // Reset the state when the source changes, preserving dirty check and any current changes
    const sourceChanged =
      initialModel.connection?.id &&
      initialModel.connection?.id !== state.connection?.id;

    if (sourceChanged) {
      options.reset({
        ...state,
        ...initialModel,
        visual_query_filter:
          initialModel.query_type === QueryType.Visual
            ? (initialModel.visual_query_filter ?? { conditions: [] })
            : null,
        query_raw_sql: null,
        query_table_name: null,
        query_dbt_model_id: null,
        query_looker_look_id: null,
        query_integrations: null,
        custom_query: null,
        query_type: initialModel.query_type ?? null,
      });
    } else if (!resetOnlyOnSourceChange) {
      reset(
        {
          ...state,
          ...initialModel,
        },
        {
          // Frontend sometimes creates undefined keys that get cleared before making it to the backend
          // We need to clear them here to prevent spurious dirty checks on data load/refresh
          visual_query_filter: removeUndefinedKeysDeep(
            // If the source did not change, we want to preserve any in-progress edits to the visual query filter
            // but we use the size cap from the backend because it's edited separately from the VQF.
            // Both sizeCap and additionalColumns (trait enrichments) are persisted immediately via UpdateAudience, so they
            // must come from the server, not the deferred-save local snapshot — otherwise a subsequent
            // "Save audience" would overwrite the server and wipe the newly created trait.
            processVisualQueryFilter(state.visual_query_filter, {
              sizeCap: initialModel.visual_query_filter.sizeCap,
              additionalColumns:
                initialModel.visual_query_filter.additionalColumns,
            }),
          ),
          // Preserve an in-progress subset edit across a refetch, but only when
          // it genuinely differs (order-insensitively) from the saved selection.
          // Otherwise an order-shuffled-but-equal copy leaves isDirty stuck.
          ...(state.subsets !== undefined &&
            !subsetIdsEqual(
              getSubsetIds(state.subsets),
              getSubsetIds(initialModel.subsets),
            ) && { subsets: state.subsets }),
        },
      );
    }
    // Want to be very careful not to trigger this useEffect more than intended
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression
  }, [initialModel]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  return useMemo(
    () => ({
      ...options,
      reset,
      set,
      state,
      onChange,
      isPreviewFresh,
      setIsPreviewFresh,
      isDirty,
      isValid,
    }),
    [
      state,
      onChange,
      reset,
      set,
      options,
      isDirty,
      isValid,
      isPreviewFresh,
      setIsPreviewFresh,
    ],
  );
};
